import type { CaptionCue } from '../captions/types';
import { invertRanges, normalizeRanges, range, subtractRanges, TIME_EPSILON, type TimeRange } from '../time/ranges';
import { MIN_LAYER_SEC } from './layers';
import { remapSpeeds } from './speed';
import type { EditRecipe } from './recipe';
import { effectiveClips, layoutSequence, remapRanges, remapTime, type Clip, type ClipSource, type SequenceLayout, type SourceLookup } from './sequence';
import type { Size } from '../geometry/framing';

/**
 * Re-times everything made on the row after its clips changed: cuts, trim,
 * caption lines, chapters and layers follow the clip they were made on.
 * Whatever sat on a clip that was taken out goes with it (undo brings both back).
 */
export function remapRecipe(recipe: EditRecipe, before: SequenceLayout, after: SequenceLayout): EditRecipe {
  const ranges = (list: readonly TimeRange[]) => remapRanges(before, after, list);
  const { trim, cuts } = remapTrim(recipe, before, after, ranges);
  return {
    ...recipe,
    trim,
    cuts,
    speeds: remapSpeeds(recipe.speeds, ranges),
    captions: { ...recipe.captions, cues: recipe.captions.cues.flatMap((cue) => remapCue(cue, before, after)) },
    chapters: recipe.chapters
      .flatMap((chapter) => {
        const start = remapTime(before, after, chapter.start);
        return start === null ? [] : [{ ...chapter, start }];
      })
      .sort((a, b) => a.start - b.start),
    layers: recipe.layers.flatMap((layer) => {
      const start = remapTime(before, after, layer.start);
      if (start === null) return [];
      const end = Math.min(after.duration, start + (layer.end - layer.start));
      return end - start >= MIN_LAYER_SEC ? [{ ...layer, start, end }] : [];
    }),
  };
}

/**
 * The trim follows its content too. What was trimmed away may end up in the
 * middle of the row (its clip moved): those parts become cuts, so nothing
 * that was removed comes back, and new clips are never trimmed away.
 */
function remapTrim(recipe: EditRecipe, before: SequenceLayout, after: SequenceLayout, ranges: (list: readonly TimeRange[]) => TimeRange[]) {
  const cuts = ranges(recipe.cuts);
  if (!recipe.trim) return { trim: null, cuts };
  const removed = ranges(invertRanges([recipe.trim], range(0, before.duration)));
  if (removed.length === 0) return { trim: null, cuts };
  const head = removed.find((part) => part.start <= TIME_EPSILON);
  const tail = removed.find((part) => part.end >= after.duration - TIME_EPSILON);
  const start = head?.end ?? 0;
  const end = tail?.start ?? after.duration;
  if (end - start <= TIME_EPSILON) return { trim: null, cuts: normalizeRanges([...cuts, ...removed]) };
  const inside = subtractRanges(removed, [...(head ? [head] : []), ...(tail ? [tail] : [])]);
  const trim = start <= TIME_EPSILON && end >= after.duration - TIME_EPSILON ? null : range(start, end);
  return { trim, cuts: normalizeRanges([...cuts, ...inside]) };
}

function remapCue(cue: CaptionCue, before: SequenceLayout, after: SequenceLayout): CaptionCue[] {
  const [first] = remapRanges(before, after, [range(cue.start, cue.end)]);
  if (!first) return [];
  const words = cue.words.flatMap((word) => {
    const start = remapTime(before, after, word.start);
    if (start === null || start < first.start - TIME_EPSILON || start >= first.end) return [];
    return [{ ...word, start, end: Math.min(first.end, start + (word.end - word.start)) }];
  });
  return [{ ...cue, start: first.start, end: first.end, words }];
}

/** What a change of the row needs to know besides the recipe. */
export interface ArrangeContext {
  readonly lookup: SourceLookup;
  readonly frame: Size;
  /** The opened video, standing in for the row while the recipe has no clips of its own. */
  readonly primary: ClipSource | undefined;
}

export const layoutOf = (recipe: Pick<EditRecipe, 'sequence'>, context: ArrangeContext): SequenceLayout =>
  layoutSequence(effectiveClips(recipe.sequence.clips, context.primary), context.lookup, context.frame);

/** Gives the recipe a new row of clips, moving everything made on the row along with its clips. */
export function arrange(recipe: EditRecipe, clips: readonly Clip[], context: ArrangeContext): EditRecipe {
  const before = layoutOf(recipe, context);
  const after = layoutSequence(clips, context.lookup, context.frame);
  if (after.clips.length === 0) return recipe;
  return { ...remapRecipe(recipe, before, after), sequence: { clips: [...clips] } };
}
